import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Video, Building, FileText, Download, Loader2, User, Phone, Mail, Search, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getAdminAppointments, getAdminReports } from '../services/api';

const STATUS_COLORS = {
  REQUESTED: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-blue-100 text-blue-800',
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  RESCHEDULED: 'bg-purple-100 text-purple-800',
  REJECTED: 'bg-red-100 text-red-800',
  COMPLETED: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
};

const PAYMENT_COLORS = {
  PENDING: 'bg-amber-100 text-amber-800',
  PAID_ONLINE: 'bg-emerald-100 text-emerald-800',
  PAYMENT_PENDING: 'bg-blue-100 text-blue-800',
};

const AdminDashboard = () => {
  const [secret, setSecret] = useState('');
  const [inputSecret, setInputSecret] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [reports, setReports] = useState({});
  const [loadingReports, setLoadingReports] = useState({});

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDateTime = (dt) => {
    if (!dt) return '';

    // Backend stores booking timestamps in UTC, but some records may be
    // serialized without an explicit timezone. Treat those as UTC so admin
    // always sees the exact booking time in clinic timezone (IST).
    const rawValue = String(dt);
    const hasTimeZone = /([zZ]|[+-]\d{2}:\d{2})$/.test(rawValue);
    const normalizedValue = hasTimeZone ? rawValue : `${rawValue}Z`;

    return new Date(normalizedValue).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const fetchAppointments = async (s) => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminAppointments(s);
      setAppointments(data.appointments);
      setFiltered(data.appointments);
      setSecret(s);
    } catch (e) {
      setError('Invalid secret key or server error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = appointments;
    if (statusFilter !== 'ALL') result = result.filter(a => a.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.patient_name?.toLowerCase().includes(q) ||
        a.patient_email?.toLowerCase().includes(q) ||
        a.patient_phone?.includes(q) ||
        a.booking_id?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, statusFilter, appointments]);

  const loadReports = async (bookingId) => {
    if (reports[bookingId]) return;
    setLoadingReports(prev => ({ ...prev, [bookingId]: true }));
    try {
      const data = await getAdminReports(bookingId, secret);
      setReports(prev => ({ ...prev, [bookingId]: data.reports }));
    } catch (e) {
      setReports(prev => ({ ...prev, [bookingId]: [] }));
    } finally {
      setLoadingReports(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const toggleExpand = (bookingId, hasReports) => {
    if (expandedId === bookingId) {
      setExpandedId(null);
    } else {
      setExpandedId(bookingId);
      if (hasReports) loadReports(bookingId);
    }
  };

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => ['REQUESTED', 'PENDING'].includes(a.status)).length,
    confirmed: appointments.filter(a => a.status === 'CONFIRMED').length,
    completed: appointments.filter(a => a.status === 'COMPLETED').length,
    revenue: appointments.filter(a => a.payment_status === 'PAID_ONLINE').reduce((s, a) => s + (a.amount || 0), 0),
  };

  if (!secret) {
    return (
      <div className="min-h-screen bg-[#FDF9F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-serif text-[#2F5233] text-center">Admin Dashboard</CardTitle>
            <p className="text-center text-gray-500 text-sm">Enter your admin secret key to access</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="password"
              placeholder="Admin secret key"
              value={inputSecret}
              onChange={e => setInputSecret(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchAppointments(inputSecret)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5233]"
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button
              className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90"
              onClick={() => fetchAppointments(inputSecret)}
              disabled={loading || !inputSecret}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Access Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF9F5] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif font-bold text-[#2F5233]">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">JB's Homoeopathy — All Appointments</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchAppointments(secret)} className="mt-4 md:mt-0">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-900' },
            { label: 'Pending', value: stats.pending, color: 'text-amber-600' },
            { label: 'Confirmed', value: stats.confirmed, color: 'text-emerald-600' },
            { label: 'Completed', value: stats.completed, color: 'text-blue-600' },
            { label: 'Revenue', value: `₹${stats.revenue.toLocaleString()}`, color: 'text-[#2F5233]' },
          ].map(s => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, booking ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5233]"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F5233]"
          >
            <option value="ALL">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2F5233] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No appointments found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filtered.map(apt => (
              <Card key={apt.booking_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Left: patient info */}
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${apt.consultation_type === 'ONLINE' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                        {apt.consultation_type === 'ONLINE'
                          ? <Video className="w-6 h-6 text-blue-600" />
                          : <Building className="w-6 h-6 text-emerald-600" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-gray-900">{apt.patient_name}</p>
                          <Badge className={STATUS_COLORS[apt.status]}>{apt.status}</Badge>
                          <Badge className={PAYMENT_COLORS[apt.payment_status] || 'bg-gray-100 text-gray-600'}>
                            {apt.payment_status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{apt.booking_id}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{apt.patient_email}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{apt.patient_phone}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(apt.appointment_date)}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(apt.time_slot)}</span>
                          <span className="font-semibold text-[#2F5233]">₹{apt.amount?.toLocaleString()}</span>
                        </div>
                        {apt.reason && <p className="text-sm text-gray-400 mt-1 italic">"{apt.reason}"</p>}
                        <p className="text-xs text-gray-400 mt-1">Booked: {formatDateTime(apt.created_at)}</p>
                      </div>
                    </div>

                    {/* Right: reports button */}
                    <div className="flex items-center gap-2">
                      {apt.uploaded_reports?.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleExpand(apt.booking_id, true)}
                          className="text-[#2F5233] border-[#2F5233]"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {apt.uploaded_reports.length} Report(s)
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Reports drawer */}
                  {expandedId === apt.booking_id && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Uploaded Reports</p>
                      {loadingReports[apt.booking_id] ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Generating download links...
                        </div>
                      ) : reports[apt.booking_id]?.length > 0 ? (
                        <div className="space-y-2">
                          {reports[apt.booking_id].map((report, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-[#2F5233]" />
                                <span className="text-sm font-medium">{report.file_name}</span>
                              </div>
                              <a href={report.download_url} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" className="bg-[#2F5233] hover:bg-[#2F5233]/90">
                                  <Download className="w-4 h-4 mr-1" /> Download
                                </Button>
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No downloadable reports found.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
